/**
 * Anchor — tag `<a>`. Sur clic, route vers un handler par `t` (form/i/cpy/url/
 * showAppMenu/default). Soft-handoff : si `data-app` / `data-ha-set` est présent,
 * pas de handler — le controller (SPA / ScopeController) gère (review 2026-05-24).
 */

import { Tag } from "./Tag";
import { toMap, log2, getWebPageService, getAppService } from "../../../HA";
import { handleAnchorForm } from "./AnchorFormHandler";
import { AnchorInteractiveHandler } from "./AnchorInteractiveHandler";
import { copyToClipboard } from "./AnchorCopyHandler";

export class Anchor extends Tag {

    private _interactive?: AnchorInteractiveHandler;

    public getTagName(): string {
        return "a";
    }

    public getTag(): Tag {
        const tag = this.get("tag");
        if (tag) { return tag; }
        if (this instanceof Tag) { return this; }
        return null;
    }

    protected initialize(data: any) {
        super.initialize(data);
        if (data.get("d")) {
            this.addAttribute("d", data.get("d"));
            this.addAttribute("t", data.get("t"));
            this.addAttribute("target", data.get("target"));
        }
        if (data.get("href")) {
            this.addAttribute("href", data.get("href"));
        }
        const el = this.getElement();
        if (el && (el.hasAttribute("data-app") || el.hasAttribute("data-ha-set"))) {
            return;
        }
        this.onClick((event: MouseEvent) => {
            event.stopImmediatePropagation();
            this.processClick();
        });
        this.bindKeyboardActivation(el);
    }

    /**
     * HA-268 — un `<a>` d'action sans `href` n'est ni focusable ni activable au
     * clavier (le navigateur ne lui donne ni tabulation ni clic sur Entrée) :
     * menu ⋯ de la médiathèque et entrées d'AppMenu étaient inatteignables.
     * Un lien avec `href` garde le comportement natif.
     */
    private bindKeyboardActivation(el: Element | null): void {
        if (!el || el.hasAttribute("href")) { return; }
        if (!el.hasAttribute("tabindex")) { el.setAttribute("tabindex", "0"); }
        if (!el.hasAttribute("role")) { el.setAttribute("role", "button"); }
        el.addEventListener("keydown", (event: Event) => {
            const key = (event as KeyboardEvent).key;
            if (key !== "Enter" && key !== " ") { return; }
            event.preventDefault();
            event.stopImmediatePropagation();
            this.processClick();
        });
    }

    protected getType() {
        return this.getTag().getAttribute("t");
    }

    protected getTarget(): string {
        let target = this.getTag().getAttribute("target");
        if (!target) {
            target = this.has("target") ? this.get("target") : "_blank";
        }
        return target;
    }

    protected getCData() {
        const d = this.getTag().getAttribute("d");
        try {
            return toMap(JSON.parse(d));
        } catch (e) {
            log2(e);
            return d;
        }
    }

    protected getMData() {
        const d = this.getTag().getAttribute("d");
        try {
            const da = JSON.parse(d);
            if (!da) { return []; }
            if (typeof da === "string") { return da; }
            return da.map((e: any) => typeof e === "string" ? e : toMap(e));
        } catch (e) {
            return d;
        }
    }

    protected notify() {
        const n = this.get("n");
        if (n && n instanceof Function) { n(); }
    }

    protected processClick() {
        this.notify();
        const data = this.getCData();
        switch (this.getType()) {
            case "form":
                handleAnchorForm(data);
                break;
            case "i":
                this.getInteractive().handle(data);
                break;
            case "showAppMenu":
                getWebPageService().showAppMenu(this.getMData());
                break;
            case "url":
                window.open("" + this.getMData(), this.getTarget());
                break;
            case "cpy":
                copyToClipboard(this.getMData() as string);
                break;
            default:
                (data as any).set("t", this.getType());
                this.mergeHrefParams(data as Map<any, any>);
                getAppService().processAction(data as Map<any, any>);
                break;
        }
    }

    /**
     * ha/508 — Transporte la référence d'un lien détail vers la nav SPA.
     *
     * Le converter réécrit `<a href="/admin/agency/devis-detail">` en
     * `t="showApp" d='{"id":"…devis-detail"}'` : le payload ne porte que le
     * pageId. La référence humaine (`?reference=D-100010`) n'arrive dans le
     * `href` qu'au runtime via `data-bind-href` (hydratation), donc le seul
     * endroit où elle est concrète est l'attribut `href` du DOM au clic.
     *
     * On replie la query du href dans le payload :
     *  - `d`   = JSON des paramètres → canal SPA serveur (MockupPageController
     *            → `$request->set('reference', …)` → hydratation du record).
     *  - `_url` = href complet → barre d'adresse + refresh.
     *  - `_id`  = `<pageId>::<params>` → IDENTITÉ d'instance côté client.
     *            Sans ça, AppStack/WebPage indexent par le seul `pageId`
     *            (`findApp` court-circuite avant le serveur) : un 2ᵉ clic vers
     *            le même pageId détail mais une autre référence réafficherait
     *            l'instance déjà chargée. `_id` est le discriminant que
     *            `isSameApp`/`isActive`/cache honorent déjà.
     *
     * Sans `?`, no-op : les liens sans paramètre gardent le comportement
     * actuel. Général : couvre tous les liens détail (client→devis,
     * prospect→devis, →client, fil d'Ariane…).
     */
    private mergeHrefParams(data: Map<any, any>): void {
        if (!(data instanceof Map)) { return; }
        const el = this.getElement() as Element | null;
        const href = el?.getAttribute?.("href");
        if (!href) { return; }
        const qi = href.indexOf("?");
        if (qi < 0) { return; }
        const search = href.slice(qi + 1);
        if (search === "") { return; }
        const params: Record<string, string> = {};
        new URLSearchParams(search).forEach((v, k) => { params[k] = v; });
        if (Object.keys(params).length === 0) { return; }
        const serialized = JSON.stringify(params);
        const pageId = data.get("id");
        if (!data.has("d"))    { data.set("d", serialized); }
        if (!data.has("_url")) { data.set("_url", href); }
        if (!data.has("_id") && pageId) { data.set("_id", pageId + "::" + serialized); }
    }

    private getInteractive(): AnchorInteractiveHandler {
        return this._interactive
            ?? (this._interactive = new AnchorInteractiveHandler({ findById: (id, deep) => this.findById(id, deep) }));
    }
}
